import { gql } from '@apollo/client';
import React from 'react';
import { createContext } from 'react';
import { useHotToast } from 'src/utils/useHotToast';
import {
  ListRequestedAppointmentsDocument,
  ListRequestedAppointmentsQuery,
  useGetAppointmentUpdatesSubscription,
  useListRequestedAppointmentsQuery,
} from './__generated__/useAppointmentsRequestContext.generated';
import { WorkOrderAppointmentRowFragmentDoc } from 'src/Components/HomePage/WorkOrderCalendarView/AppointmentRow/__generated__/WorkOrderAppointmentRow.generated';
import { useApolloCacheUpdater } from 'src/utils/useApolloCacheUpdater';

export const AppointmentsRequestContext = createContext<{
  requestedAppointments: ListRequestedAppointmentsQuery['listRequestedAppointments'];
  isLoadingRequestedAppointments: boolean;
}>({
  requestedAppointments: [],
  isLoadingRequestedAppointments: false,
});

export function useAppointmentsRequestContext() {
  return React.useContext(AppointmentsRequestContext);
}

export function useListRequestedAppointments() {
  const toast = useHotToast();

  const { data, loading, error } = useListRequestedAppointmentsQuery({
    onError: e => {
      toast.error(e.message);
    },
  });
  return {
    requestedAppointments: data?.listRequestedAppointments ?? [],
    isLoadingRequestedAppointments: loading,
  };
}

const query = gql`
  query ListRequestedAppointments {
    listRequestedAppointments {
      ...WorkOrderAppointmentRow
    }
    ${WorkOrderAppointmentRowFragmentDoc}
  }
`;

export function useGetAppointmentUpdates() {
  const { getOldData, updateData } =
    useApolloCacheUpdater<ListRequestedAppointmentsQuery>(
      ListRequestedAppointmentsDocument,
      {}
    );
  const { data, loading, error } = useGetAppointmentUpdatesSubscription({
    onData: updatedAppointmentData => {
      const updatedAppointment =
        updatedAppointmentData.data.data?.getAppointmentUpdates;
      if (!updatedAppointment) {
        return;
      }
      const oldData = getOldData();
      if (!oldData) {
        return;
      }
      updateData({
        listRequestedAppointments: [
          ...oldData.listRequestedAppointments,
          updatedAppointment,
        ],
      });
    },
  });
  return {
    appointments: data?.getAppointmentUpdates ?? [],
    isLoading: loading,
  };
}

const subscription = gql`
subscription GetAppointmentUpdates {
  getAppointmentUpdates {
    ...WorkOrderAppointmentRow
  }
  ${WorkOrderAppointmentRowFragmentDoc}
}
`;
